import { effect, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  HTTPError,
  IOlavEntry,
  IOrderDetailsInfo,
  IProduktVerfuegbarkeit,
  IRelocationDataDTO,
  IRelocationDataWrapper,
  newRelocationDetailsInfo,
  parseILCustomerAddress,
  Product,
  RelocationConfirmationStatusResponse,
} from '@mwe/models';
import {
  getAccountOwner,
  getInvoiceAddress,
  getInvoiceEmail,
  isArrayWithMinOneItem,
  isProductCategory,
  parseInvoiceAccount,
  parseIPartnerFromClearingAccount,
  parseIRechnungsempfaengerFromClearingAccount,
  parseIVertragskontoFromClearingAccount,
  setBezahlartZahlschein,
  specialHackToCheckIfGasIsAvailable,
} from '@mwe/utils';
import { ProductCategoryEnum, ServiceStateEnum } from '@mwe/constants';
import { OrderService } from '../order/order.service';
import { EnvironmentService } from '../environment/environment.service';
import { ClearingAccountLogic } from '../invoices/clearing-account/clearing-account.logic';
import { lastValueFrom } from 'rxjs';
import { EnvironmentCustomerType } from '@mwe/environment';
import { RelocationLogic } from './relocation.logic';
import { CsvDataService } from '../address/csv-data.service';
import { LoggingService } from '../logging/logging.service';
import { RelocationStore } from './relocation.store';
import { StaticContentService } from '../static-content/static-content.service';

@Injectable({ providedIn: 'root' })
export class RelocationService {
  public resourceUrl: string;
  private readonly http = inject(HttpClient);
  private readonly relocationStore = inject(RelocationStore);
  private readonly clearingAccountLogic = inject(ClearingAccountLogic);
  private readonly relocationLogic = inject(RelocationLogic);
  private readonly orderService = inject(OrderService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly csvDataService = inject(CsvDataService);
  private readonly loggingService = inject(LoggingService);
  private readonly staticContentService = inject(StaticContentService);
  private readonly config = signal<any>(undefined);
  private readonly inkaTitleMap = new Map<string, string>();

  constructor() {
    this.resourceUrl = this.environmentService.getApiUrl() + 'api/relocation/';
    this.inkaTitleMap.set('0001', 'Frau');
    this.inkaTitleMap.set('0002', 'Herr');
    this.inkaTitleMap.set('0003', 'Firma');
    this.inkaTitleMap.set('0004', '');
    this.inkaTitleMap.set('0005', 'Frau/Herr');
    this.inkaTitleMap.set('0006', 'Rechtsanwälte');
    effect(() => {
      this.staticContentService.loadProductsConfiguration().then(staticProducts => {
        this.config.set(staticProducts);
      });
    });
  }

  private async loadStaticProducts() {
    if (!this.config()) {
      const staticProducts = await this.staticContentService.loadInternetProductsConfiguration();
      this.config.set(staticProducts);
    }
  }

  getAvailability(selectedOlav: IOlavEntry): Promise<IProduktVerfuegbarkeit[]> {
    return this.orderService.getAvailability(selectedOlav);
  }

  async prepareAvailabilities() {
    const selectedOlav = this.relocationStore.relocationToOlav();
    try {
      const response = await this.getAvailability(selectedOlav);
      if (response) {
        const fixFWAvailabilities = this.fixFWAvailabilities(response);

        const relocationToAvailabilities = await specialHackToCheckIfGasIsAvailable(
          selectedOlav,
          fixFWAvailabilities,
          () => this.csvDataService.getCoverageArea('assets/data/gas_versorgungsgebiet.csv'),
          true,
        );
        this.relocationStore.updateStoreState({ relocationToAvailabilities, relocationToAvailabilitiesState: ServiceStateEnum.SUCCESS });
      } else {
        this.relocationStore.updateStoreState({ relocationToAvailabilitiesState: ServiceStateEnum.ERROR });
      }
    } catch (error) {
      this.relocationStore.updateStoreState({ relocationToAvailabilitiesState: ServiceStateEnum.ERROR });
      this.loggingService.logError(error, `can't load/render availabilities`);
    }
  }

  setDeliveryOfContractData(sendContractDetailsToNewAddress: boolean): void {
    const currentRelocationDetails = this.relocationStore.relocationDetails();
    if (!currentRelocationDetails) return;

    const relocationDetails = currentRelocationDetails.map(productDetails => {
      if (!isProductCategory(productDetails.category, ProductCategoryEnum.FERNWAERME) && productDetails.status !== 'Anmeldung')
        return productDetails;

      return {
        ...productDetails,
        zustelladresse: sendContractDetailsToNewAddress ? 'to' : 'from',
      } as IOrderDetailsInfo;
    });

    this.relocationStore.updateStoreState({ relocationDetails });
  }

  isDeliveryOfContractDataSetNewAddress(): boolean {
    const fwDewtails = this.relocationStore
      .relocationDetails()
      .find(
        productDetails =>
          isProductCategory(productDetails.category, ProductCategoryEnum.FERNWAERME) && productDetails.status === 'Anmeldung',
      );
    return fwDewtails ? fwDewtails.zustelladresse === 'to' : false;
  }

  hasFernwaermeInNewAddressProducts(): boolean {
    return this.getNewAddressProductCategories().some(elem => isProductCategory(ProductCategoryEnum[elem], ProductCategoryEnum.FERNWAERME));
  }

  hasFernwaermeInOldAddressProducts(): boolean {
    return [...this.getNewAddressProductCategories(), ...this.getOldAddressProductCategories()].some(elem =>
      isProductCategory(ProductCategoryEnum[elem], ProductCategoryEnum.FERNWAERME),
    );
  }

  getNewAddressProductCategories(): ProductCategoryEnum[] {
    return this.relocationStore
      .relocationDetails()
      .filter(elem => elem.status === 'Anmeldung')
      .map(elem => ProductCategoryEnum[elem.category.toUpperCase()]);
  }

  getOldAddressProductCategories(): ProductCategoryEnum[] {
    return this.relocationStore
      .relocationDetails()
      .filter(elem => elem.status === 'Abmeldung')
      .map(elem => ProductCategoryEnum[elem.category.toUpperCase()]);
  }

  async loadDetailsData(
    products: Product[],
    orderStatus: 'Abmeldung' | 'Anmeldung',
    zustelladresse: 'from' | 'to' | '',
  ): Promise<IOrderDetailsInfo[]> {
    if (!products) return [];

    const customerType = this.environmentService.getPortalCustomerType();
    const newDetails: IOrderDetailsInfo[] = [];

    for (const product of products) {
      const tariffObj = this.updateTariffObj(orderStatus, product, customerType);
      const orderDetailData = this.getOrderDetailData(orderStatus, zustelladresse, product, tariffObj);

      if (this.isAccountNumberEmpty(product)) {
        newDetails.push(orderDetailData);
        continue;
      }

      const alreadyLoadedDetails = this.getAlreadyLoadedDetails(newDetails, product);

      try {
        const verrechnungskonto = await this.getVerrechnungskonto(alreadyLoadedDetails, product);
        const accountCustomer = getAccountOwner(verrechnungskonto?.geschaeftspartnerBeziehungen);
        const invoiceEmail = getInvoiceEmail(verrechnungskonto);
        const rechnungsempfaenger = parseIRechnungsempfaengerFromClearingAccount(verrechnungskonto);

        const updatedOrderDetailsData = {
          ...orderDetailData,
          verrechnungskonto,
          physischePerson: accountCustomer?.physischePerson,
          invoiceAddress: parseILCustomerAddress(getInvoiceAddress(verrechnungskonto?.geschaeftspartnerBeziehungen)),
          eRechnungInfo: {
            isERechnungsEmpfaenger: !!invoiceEmail,
            rechnungEmail: invoiceEmail,
          },
          produktDetails: {
            partner: parseIPartnerFromClearingAccount(verrechnungskonto),
            vertragskonto: parseIVertragskontoFromClearingAccount(verrechnungskonto),
            anschlussobjekt: null,
            rechnungsempfaenger,
            kontostand: null,
            ablesung: null,
            verbraeuche: null,
            teilbetragsplaene: null,
            rechnungen: null,
          },
        };

        const orderDetailsData = parseInvoiceAccount(updatedOrderDetailsData, true);
        newDetails.push(orderDetailsData);
      } catch (e) {
        throw new Error('general_error', e);
      }
    }

    return this.updateIfStatusIsNewOrder(orderStatus, newDetails);
  }

  overrideBezahlartForFernwaermeRegistration(relocationDetails: IOrderDetailsInfo[]) {
    return relocationDetails
      .filter(relocationDetail => isProductCategory(relocationDetail.category, ProductCategoryEnum.FERNWAERME))
      .map(fernwaermeDetail => {
        return setBezahlartZahlschein(fernwaermeDetail, true);
      });
  }

  showUseFernwaermeDetails(): boolean {
    return (
      this.relocationStore.relocationFromProducts().length === 1 &&
      isProductCategory(this.relocationStore.relocationFromProducts()[0].category, ProductCategoryEnum.FERNWAERME)
    );
  }

  async confirmRelocation(): Promise<RelocationConfirmationStatusResponse> {
    try {
      const dto = await this.relocationLogic.getRelocationDTOV2();
      const res = await lastValueFrom(this.http.post<RelocationConfirmationStatusResponse>(this.resourceUrl + 'confirm', dto));

      this.relocationStore.updateStoreState({ submitId: res.submitId });

      return res;
    } catch (err) {
      throw new HTTPError(err.status, err.error);
    }
  }

  async confirmStatus(submitId: number): Promise<RelocationConfirmationStatusResponse> {
    try {
      return lastValueFrom(this.http.get<RelocationConfirmationStatusResponse>(this.resourceUrl + 'confirmStatus?submitId=' + submitId));
    } catch (err) {
      throw new HTTPError(err.status, err.error);
    }
  }

  determineSummaryDetailsAddressGroupId(): string {
    return this.relocationStore.relocationFromAddress()?.addressGroup
      ? this.relocationStore.relocationFromAddress().addressGroup.id
      : this.relocationStore.lastOrderID();
  }

  getRelocationSummaryDetailsFor(addressGroupId: string): Promise<IRelocationDataWrapper> {
    return lastValueFrom(
      this.http.get<IRelocationDataWrapper>(this.resourceUrl + 'relocationSummaryDetails?addressGroupId=' + addressGroupId),
    );
  }

  getParsedRelocationSummaryDetailsFor(addressGroupId: string): Promise<IRelocationDataDTO> {
    return this.getRelocationSummaryDetailsFor(addressGroupId).then(res => this.parseRelocationDataDTO(res));
  }

  getConfigPossibleCategories(): any {
    return this.config()?.possibleCategories;
  }

  getConfigAvailabilityForCategory(category: string): any {
    return this.config()?.availability[category];
  }

  getConfigTariffs(): any {
    return this.config()?.tariffs;
  }

  getConfigTariffsForCategory(category: string): any {
    return this.config()?.tariffs?.[category];
  }

  parseRelocationDataDTO(relocationDetails: IRelocationDataWrapper): IRelocationDataDTO {
    if (relocationDetails?.version === 'V2') {
      return this.relocationLogic.parseVersionTwo(JSON.parse(relocationDetails?.changeData), relocationDetails);
    }

    return this.relocationLogic.parseVersionOne(JSON.parse(relocationDetails?.changeData), relocationDetails);
  }

  private fixFWAvailabilities(response: IProduktVerfuegbarkeit[]): IProduktVerfuegbarkeit[] {
    return response.map(product => {
      if (product.sparte !== 'Fernwaerme' || product.details?.[0]?.lieferkomponente !== 'HEIZUNG + WARMWASSER') {
        return product;
      }

      const [firstDetail, ...restDetails] = product.details;
      const clonedDetail = structuredClone(firstDetail);

      return {
        ...product,
        details: [{ ...firstDetail, lieferkomponente: 'HEIZUNG' }, { ...clonedDetail, lieferkomponente: 'WARMWASSER' }, ...restDetails],
      };
    });
  }

  private updateIfStatusIsNewOrder(orderStatus: 'Abmeldung' | 'Anmeldung', newDetails: IOrderDetailsInfo[]) {
    if (orderStatus !== 'Anmeldung') {
      return newDetails;
    }

    const updatedFW = this.overrideBezahlartForFernwaermeRegistration(newDetails);

    if (!isArrayWithMinOneItem(updatedFW)) {
      return newDetails;
    }

    return newDetails.map(detail => {
      if (!isProductCategory(detail.category, ProductCategoryEnum.FERNWAERME)) {
        return detail;
      }

      const updatedItem = updatedFW.find(fw => fw.accountNumber === detail.accountNumber);
      return updatedItem ? { ...detail, ...updatedItem } : detail;
    });
  }

  private isAccountNumberEmpty(product: Product) {
    return !product.accountNumber || product.accountNumber === '';
  }

  private updateTariffObj(orderStatus: 'Abmeldung' | 'Anmeldung', product: Product, customerType: EnvironmentCustomerType) {
    let tariffObj: { name: string; shortcut: string; key: string };

    switch (orderStatus) {
      case 'Abmeldung':
        tariffObj = { name: product.tariffClasses[0], shortcut: '', key: '' };
        break;
      case 'Anmeldung':
        tariffObj = this.config()?.tariffs?.[product.category.toLowerCase()]?.[customerType];
        break;
    }

    return tariffObj;
  }

  private async getVerrechnungskonto(alreadyLoadedDetails: IOrderDetailsInfo, product: Product) {
    return alreadyLoadedDetails?.verrechnungskonto
      ? alreadyLoadedDetails.verrechnungskonto
      : (await this.clearingAccountLogic.getAccountInfo(product.accountNumber, product.businessPartnerNumber, product.systemId))
          ?.verrechnungskonto;
  }

  private getOrderDetailData(
    orderStatus: 'Abmeldung' | 'Anmeldung',
    zustelladresse: 'from' | 'to' | '',
    product: Product,
    tariffObj: {
      name: string;
      shortcut: string;
      key: string;
    },
  ) {
    let fwProducts = [];

    if (isProductCategory(product.category, ProductCategoryEnum.FERNWAERME)) {
      fwProducts = orderStatus === 'Abmeldung' ? product.associationDetails.map(ad => ad.tarifName) : product.tariffClasses;
    }

    return newRelocationDetailsInfo(
      orderStatus,
      zustelladresse,
      product.category,
      product.businessPartnerNumber, // use business partner number in case of FW products it is different to the customer number
      product.accountNumber,
      tariffObj,
      fwProducts,
    );
  }

  private getAlreadyLoadedDetails(newDetails: IOrderDetailsInfo[], product: Product) {
    return newDetails.find(
      item =>
        item.customerNumber === product.customerNumber &&
        item.accountNumber === product.accountNumber &&
        item.category === product.category,
    );
  }
}
